import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { LogOut, Users, UserCog, Briefcase, BarChart3, FileText, MessageSquare, Loader2 } from 'lucide-react';
import { useStaffAuth } from '../../context/StaffAuthContext';
import { toast } from 'sonner';

export default function CrmLayout() {
  const nav = useNavigate();
  const { staff, loading, logout } = useStaffAuth();

  useEffect(() => {
    if (!loading && !staff) nav('/crm/login', { replace: true });
  }, [staff, loading, nav]);

  if (loading || !staff) {
    return (
      <div className="min-h-screen bg-[#F5F1E8] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#0B3B2E]" />
      </div>
    );
  }

  const initials = staff.name.split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
  const roleLabel = { admin: 'Admin', manager: 'Sales Manager', rm: 'Relationship Manager' }[staff.role] || staff.role;

  const handleLogout = () => {
    logout();
    toast.success('Signed out');
    nav('/crm/login', { replace: true });
  };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-[14px] transition-all ${
      isActive
        ? 'bg-[#0B3B2E] text-[#F5F1E8] font-medium'
        : 'text-[#0E1F1A]/75 hover:bg-[#0B3B2E]/8 hover:text-[#0B3B2E]'
    }`;

  const canReports = staff.role !== 'rm';

  return (
    <div className="min-h-screen bg-[#F5F1E8] flex">
      <aside className="hidden lg:flex w-[260px] flex-col border-r border-[#0B3B2E]/10 bg-white/40">
        <Link to="/crm" className="h-[72px] px-6 flex items-center gap-2 border-b border-[#0B3B2E]/10">
          <div className="w-9 h-9 rounded-full bg-[#0B3B2E] flex items-center justify-center">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M4 20 L12 4 L20 20" stroke="#C8944B" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div>
            <p className="font-serif text-[18px] font-semibold text-[#0B3B2E] leading-none">
              Vector<span className="text-[#C8944B]">FX</span>
            </p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#0E1F1A]/50 mt-1">CRM</p>
          </div>
        </Link>

        <nav className="flex-1 p-4 space-y-1">
          <NavLink to="/crm/leads" className={linkClass}>
            <Briefcase className="w-4 h-4" /> Leads
          </NavLink>
          <NavLink to="/crm/partners" className={linkClass}>
            <Users className="w-4 h-4" /> Referral partners
          </NavLink>
          {canReports && (
            <NavLink to="/crm/reports" className={linkClass}>
              <BarChart3 className="w-4 h-4" /> Reports
            </NavLink>
          )}
          {canReports && (
            <NavLink to="/crm/applications" className={linkClass}>
              <FileText className="w-4 h-4" /> Applications
            </NavLink>
          )}
          {canReports && (
            <NavLink to="/crm/complaints" className={linkClass} data-testid="crm-nav-complaints">
              <MessageSquare className="w-4 h-4" /> Complaints
            </NavLink>
          )}
          {staff.role === 'admin' && (
            <NavLink to="/crm/staff" className={linkClass}>
              <UserCog className="w-4 h-4" /> Team
            </NavLink>
          )}
        </nav>

        <div className="p-4 border-t border-[#0B3B2E]/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#0B3B2E] text-[#C8944B] font-medium flex items-center justify-center">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-medium text-[#0B3B2E] truncate">{staff.name}</p>
              <p className="text-[11px] text-[#0E1F1A]/55 truncate">{roleLabel}</p>
            </div>
            <button onClick={handleLogout} className="text-[#0E1F1A]/55 hover:text-[#0B3B2E]" title="Sign out">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 bg-white border-b border-[#0B3B2E]/10 flex items-center justify-between px-4">
        <Link to="/crm" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#0B3B2E] flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M4 20 L12 4 L20 20" stroke="#C8944B" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="font-serif text-[15px] font-semibold text-[#0B3B2E]">CRM</span>
        </Link>
        <button onClick={handleLogout} className="text-[13px] text-[#0B3B2E]">Sign out</button>
      </div>

      <div className="lg:hidden fixed top-14 inset-x-0 z-30 bg-white border-b border-[#0B3B2E]/10 flex overflow-x-auto">
        <NavLink to="/crm/leads" className={({ isActive }) => `px-4 py-2.5 text-[13px] whitespace-nowrap ${isActive ? 'text-[#0B3B2E] font-medium border-b-2 border-[#0B3B2E]' : 'text-[#0E1F1A]/60'}`}>Leads</NavLink>
        <NavLink to="/crm/partners" className={({ isActive }) => `px-4 py-2.5 text-[13px] whitespace-nowrap ${isActive ? 'text-[#0B3B2E] font-medium border-b-2 border-[#0B3B2E]' : 'text-[#0E1F1A]/60'}`}>Partners</NavLink>
        {canReports && (
          <NavLink to="/crm/reports" className={({ isActive }) => `px-4 py-2.5 text-[13px] whitespace-nowrap ${isActive ? 'text-[#0B3B2E] font-medium border-b-2 border-[#0B3B2E]' : 'text-[#0E1F1A]/60'}`}>Reports</NavLink>
        )}
        {canReports && (
          <NavLink to="/crm/applications" className={({ isActive }) => `px-4 py-2.5 text-[13px] whitespace-nowrap ${isActive ? 'text-[#0B3B2E] font-medium border-b-2 border-[#0B3B2E]' : 'text-[#0E1F1A]/60'}`}>Applications</NavLink>
        )}
        {canReports && (
          <NavLink to="/crm/complaints" className={({ isActive }) => `px-4 py-2.5 text-[13px] whitespace-nowrap ${isActive ? 'text-[#0B3B2E] font-medium border-b-2 border-[#0B3B2E]' : 'text-[#0E1F1A]/60'}`}>Complaints</NavLink>
        )}
        {staff.role === 'admin' && (
          <NavLink to="/crm/staff" className={({ isActive }) => `px-4 py-2.5 text-[13px] whitespace-nowrap ${isActive ? 'text-[#0B3B2E] font-medium border-b-2 border-[#0B3B2E]' : 'text-[#0E1F1A]/60'}`}>Team</NavLink>
        )}
      </div>

      <main className="flex-1 min-w-0 pt-24 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
